/**
 * Authentication store
 * Manages user authentication state and actions
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { logger } from '@/lib/logger'
import {
  identifyUser,
  resetUser,
  trackEvent,
} from '@/providers/PostHogProvider'
import type {
  User,
  AuthTokens,
  SendOTPRequest,
  VerifyOTPRequest,
} from '@/types/api'
import type { Settings } from '@/types/settings'
import type { UserRole } from '@/types/user'
import {
  sendOTP as apiSendOTP,
  sendEmailOTP as apiSendEmailOTP,
  verifyOTP as apiVerifyOTP,
  verifyEmailOTP as apiVerifyEmailOTP,
  googleLogin as apiGoogleLogin,
  refreshToken as apiRefreshToken,
  logout as apiLogout,
} from '@/services/auth'
import { getSettings as apiGetSettings } from '@/services/settings/api'
import {
  isModerator,
  isEditor,
  isAdmin,
  isOwner,
  getRoleName,
} from '@/helpers/users'
import { clearCsrfToken } from '@/lib/csrf'
import { BUILDER_STORAGE_KEY } from '@/stores/builder'
import { collectUserPreferences } from '@/services/auth/helpers'

type AuthMethod = 'phone_otp' | 'email_otp' | 'google'
type SentryModule = typeof import('@sentry/nextjs')
type SentryModuleLike = SentryModule | { default: SentryModule }

let sentryModulePromise: Promise<SentryModuleLike | null> | null = null

function setSentryUserContext(user: User | null): void {
  if (!sentryModulePromise) {
    sentryModulePromise = import('@sentry/nextjs').catch((error) => {
      logger.warn('Failed to load Sentry for auth context:', error)
      sentryModulePromise = null
      return null
    })
  }

  void sentryModulePromise.then((Sentry) => {
    if (!Sentry) return
    const sentryApi = 'setUser' in Sentry ? Sentry : Sentry.default
    if (!user) {
      sentryApi.setUser(null)
      return
    }
    sentryApi.setUser({
      id: user.id,
      email: user.email || undefined,
      username: user.name || undefined,
    })
  })
}

/** Post-login: Set Sentry/PostHog context and track analytics */
function handleSuccessfulAuth(user: User, method: AuthMethod): void {
  // Sentry user context
  setSentryUserContext(user)

  // PostHog user identity
  identifyUser(user.id, {
    email: user.email,
    name: user.name,
    phone: user.phone,
    created_at: user.createdAt,
  })

  // Track login/signup (new user = created within last minute)
  const isNewUser =
    !user.createdAt || Date.now() - new Date(user.createdAt).getTime() < 60000

  trackEvent('login_completed', { method, is_new_user: isNewUser })
  if (isNewUser) trackEvent('signup_completed', { method })
}

export interface AuthState {
  // State
  user: User | null
  settings: Settings | null // User settings from backend
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  otpSent: boolean
  otpPhone: string | null
  otpEmail: string | null // Added for email OTP
  _hasHydrated: boolean // Track if store has been rehydrated from localStorage
  showWelcomeBackModal: boolean // Show modal when deleted account logs in
  pendingWelcomeToast: boolean // Show notification count toast after login (consumed once)

  // Actions
  sendOTP: (data: SendOTPRequest) => Promise<void>
  sendEmailOTP: (email: string) => Promise<void> // Added for email OTP
  verifyOTP: (data: VerifyOTPRequest) => Promise<void>
  verifyEmailOTP: (
    email: string,
    otp: string,
    rememberMe?: boolean
  ) => Promise<void> // Added for email OTP verification
  googleLogin: (idToken: string, rememberMe?: boolean) => Promise<void> // Google OAuth login
  logout: (logoutAll?: boolean) => void
  refreshAuth: () => Promise<void>
  clearError: () => void
  setUser: (user: User | null) => void
  setSettings: (settings: Settings | null) => void
  refreshSettings: () => Promise<void> // Fetch latest settings from backend
  resetOTP: () => void
  setHasHydrated: (state: boolean) => void // For internal use
  setShowWelcomeBackModal: (show: boolean) => void
  clearPendingWelcomeToast: () => void // Clear the pending toast flag after showing
  restoreSession: (user: User, accessToken: string) => void // Restore session from validated cookie

  // Role helpers
  getUserRole: () => UserRole | null
  isModerator: () => boolean
  isEditor: () => boolean
  isAdmin: () => boolean
  isOwner: () => boolean
  getRoleName: () => string
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      settings: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      otpSent: false,
      otpPhone: null,
      otpEmail: null,
      _hasHydrated: false,
      showWelcomeBackModal: false,
      pendingWelcomeToast: false,

      // Set hydration state (internal use)
      setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),

      // Set welcome back modal visibility
      setShowWelcomeBackModal: (show: boolean) =>
        set({ showWelcomeBackModal: show }),

      // Clear pending welcome toast (called after showing the toast)
      clearPendingWelcomeToast: () => set({ pendingWelcomeToast: false }),

      // Send OTP action
      sendOTP: async (data: SendOTPRequest) => {
        set({ isLoading: true, error: null })
        try {
          await apiSendOTP(data)
          set({
            otpSent: true,
            otpPhone: data.phone,
            otpEmail: null,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          set({
            otpSent: false,
            otpPhone: null,
            isLoading: false,
            error:
              error instanceof Error ? error.message : 'Failed to send OTP',
          })
          throw error
        }
      },

      // Send Email OTP action
      sendEmailOTP: async (email: string) => {
        set({ isLoading: true, error: null })
        try {
          await apiSendEmailOTP({ email })
          set({
            otpSent: true,
            otpEmail: email,
            otpPhone: null,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          set({
            otpSent: false,
            otpEmail: null,
            isLoading: false,
            error:
              error instanceof Error
                ? error.message
                : 'Failed to send email OTP',
          })
          throw error
        }
      },

      verifyOTP: async (data: VerifyOTPRequest) => {
        set({ isLoading: true, error: null })
        try {
          // Collect user preferences to send with OTP verification
          const prefs = collectUserPreferences()

          // Send OTP verification with user preferences
          const response = await apiVerifyOTP({
            ...data,
            ...prefs,
          })

          // Response from backend: { success, message, data: { user, settings, accessToken, refreshToken } }
          const user = response.data.user
          const settings = response.data.settings || null

          // Handle different token response formats
          const tokens: AuthTokens = {
            accessToken: response.data.accessToken || '',
            refreshToken: response.data.refreshToken || '',
          }

          set({
            user,
            settings,
            tokens,
            isAuthenticated: true,
            otpSent: false,
            otpPhone: null,
            otpEmail: null,
            isLoading: false,
            error: null,
            showWelcomeBackModal: !!user?.deletedAt, // Show modal if deletedAt exists
            pendingWelcomeToast: true, // Trigger welcome toast after notifications load
          })

          // If settings weren't returned, fetch them
          if (!settings) {
            try {
              const fetchedSettings = await apiGetSettings()
              set({ settings: fetchedSettings })
            } catch (error) {
              logger.warn('Failed to fetch settings after login:', error)
            }
          }

          // Handle Sentry, PostHog, and analytics
          handleSuccessfulAuth(user, 'phone_otp')
        } catch (error) {
          trackEvent('login_failed', { method: 'phone_otp' })
          set({
            user: null,
            settings: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Invalid OTP code',
          })
          throw error
        }
      },

      // Verify Email OTP action
      verifyEmailOTP: async (
        email: string,
        otp: string,
        rememberMe?: boolean
      ) => {
        set({ isLoading: true, error: null })
        try {
          // Collect user preferences to send with OTP verification
          const prefs = collectUserPreferences()

          // Send email OTP verification with user preferences
          const response = await apiVerifyEmailOTP({
            email,
            otp,
            ...prefs,
            rememberMe,
          })

          // Response is wrapped: { success, message, data: { user, settings, accessToken, refreshToken } }
          const user = response.data.user
          const settings = response.data.settings || null

          // Handle different token response formats
          const tokens: AuthTokens = {
            accessToken: response.data.accessToken || '',
            refreshToken: response.data.refreshToken || '',
          }

          set({
            user,
            settings,
            tokens,
            isAuthenticated: true,
            otpSent: false,
            otpPhone: null,
            otpEmail: null,
            isLoading: false,
            error: null,
            showWelcomeBackModal: !!user?.deletedAt, // Show modal if deletedAt exists
            pendingWelcomeToast: true, // Trigger welcome toast after notifications load
          })

          // If settings weren't returned, fetch them
          if (!settings) {
            try {
              const fetchedSettings = await apiGetSettings()
              set({ settings: fetchedSettings })
            } catch (error) {
              logger.warn('Failed to fetch settings after email login:', error)
            }
          }

          // Handle Sentry, PostHog, and analytics
          handleSuccessfulAuth(user, 'email_otp')
        } catch (error) {
          trackEvent('login_failed', { method: 'email_otp' })
          set({
            user: null,
            settings: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error:
              error instanceof Error
                ? error.message
                : 'Invalid email verification code',
          })
          throw error
        }
      },

      // Google OAuth Login
      googleLogin: async (idToken: string, rememberMe?: boolean) => {
        set({ isLoading: true, error: null })
        try {
          // Collect user preferences
          const prefs = collectUserPreferences()

          // Send Google login request
          const response = await apiGoogleLogin({
            idToken,
            ...prefs,
            rememberMe,
          })

          // Response is wrapped: { success, message, data: { user, settings, accessToken, refreshToken } }
          const user = response.data.user
          const settings = response.data.settings || null

          const tokens: AuthTokens = {
            accessToken: response.data.accessToken || '',
            refreshToken: response.data.refreshToken || '',
          }

          set({
            user,
            settings,
            tokens,
            isAuthenticated: true,
            otpSent: false,
            otpPhone: null,
            otpEmail: null,
            isLoading: false,
            error: null,
            showWelcomeBackModal: !!user?.deletedAt,
            pendingWelcomeToast: true, // Trigger welcome toast after notifications load
          })

          // Handle Sentry, PostHog, and analytics
          handleSuccessfulAuth(user, 'google')
        } catch (error) {
          trackEvent('login_failed', { method: 'google' })
          set({
            user: null,
            settings: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error:
              error instanceof Error ? error.message : 'Google login failed',
          })
          throw error
        }
      },

      // Reset OTP state
      resetOTP: () =>
        set({ otpSent: false, otpPhone: null, otpEmail: null, error: null }),

      logout: (logoutAll = false) => {
        const { tokens } = get()

        // Best-effort server invalidation (keepalive survives immediate redirect).
        // Non-blocking so logout UX stays instant even on network failure.
        apiLogout(tokens?.refreshToken ?? undefined, logoutAll).catch((err) =>
          logger.warn(
            '⚠️ [Auth] Backend logout call failed (non-critical):',
            err
          )
        )

        // Clear auth state synchronously before navigation to avoid auth-gate races.
        set({
          user: null,
          settings: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })

        setSentryUserContext(null)
        trackEvent('logout')
        resetUser()
        clearCsrfToken()

        // Wipe persisted state (important for shared/public devices)
        localStorage.removeItem('dday-auth-storage')
        localStorage.removeItem(BUILDER_STORAGE_KEY)
        localStorage.removeItem('dday-stationery-builder-storage')
        localStorage.removeItem('dday-user-theme')
        localStorage.removeItem('dday-user-timezone')
        sessionStorage.removeItem('invitation-guest-draft')

        // Clear client-accessible cookies only; httpOnly refresh cookie is server-cleared.
        document.cookie.split(';').forEach((cookie) => {
          const eqPos = cookie.indexOf('=')
          const name =
            eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`
        })
      },

      refreshAuth: async () => {
        const { tokens } = get()
        if (!tokens) {
          get().logout()
          throw new Error('No auth tokens available for refresh')
        }

        set({ isLoading: true })
        try {
          const response = await apiRefreshToken(
            tokens.refreshToken || undefined
          )
          set({
            tokens: {
              ...tokens,
              accessToken: response.accessToken,
            },
            isLoading: false,
          })
        } catch (error) {
          set({ isLoading: false })
          get().logout()
          throw error
        }
      },

      clearError: () => set({ error: null }),
      setUser: (user) => set({ user }),
      setSettings: (settings) => set({ settings }),

      // Fetch latest settings from backend
      // Used after invitation mutations that sync user settings
      refreshSettings: async () => {
        try {
          const settings = await apiGetSettings()
          set({ settings })
        } catch (error) {
          logger.warn('Failed to refresh settings:', error)
          // Don't throw - settings refresh is not critical
        }
      },

      // Restore session from validated cookie
      // Used when localStorage was cleared but the httpOnly cookie is still valid
      restoreSession: (user: User, accessToken: string) => {
        set({
          user,
          tokens: { accessToken, refreshToken: '' }, // refreshToken is httpOnly cookie
          isAuthenticated: true,
          isLoading: false,
          error: null,
        })

        // Set Sentry user context for error tracking
        setSentryUserContext(user)

        // Set PostHog user identity for analytics
        identifyUser(user.id, {
          email: user.email,
          name: user.name,
          created_at: user.createdAt,
        })

        // Track session restore event
        trackEvent('session_restored', { method: 'cookie_validation' })
      },

      getUserRole: () => {
        const { user } = get()
        return (user?.role as UserRole) ?? null
      },

      isModerator: () => {
        const { user } = get()
        return user ? isModerator(user.role as UserRole) : false
      },

      isEditor: () => {
        const { user } = get()
        return user ? isEditor(user.role as UserRole) : false
      },

      isAdmin: () => {
        const { user } = get()
        return user ? isAdmin(user.role as UserRole) : false
      },

      isOwner: () => {
        const { user } = get()
        return user ? isOwner(user.role as UserRole) : false
      },

      getRoleName: () => {
        const { user } = get()
        return user ? getRoleName(user.role as UserRole) : 'Guest'
      },
    }),
    {
      name: 'dday-auth-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist specific fields (exclude refreshToken for security)
      partialize: (state) => ({
        user: state.user,
        settings: state.settings, // Persist settings from backend
        tokens: state.tokens
          ? {
              accessToken: state.tokens.accessToken,
              // Never store refreshToken in localStorage - it should be httpOnly cookie
            }
          : null,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Mark as hydrated even if persisted data is missing/corrupt.
        // Without this fallback, auth gates can remain in an infinite loading state.
        if (state) {
          state.setHasHydrated(true)
          return
        }

        useAuthStore.setState({ _hasHydrated: true })
      },
    }
  )
)
